import { useState, useCallback, useEffect, useRef } from 'react';
import { Header } from '@/components/layout/Header';
import { EnergyDashboard, EnergyStatusBar, MobileMenuDateControls } from '@/components/dashboard';
import { useFilters } from '@/contexts/FilterContext';
import { useLiveDemoContext } from '@/contexts/LiveDemoContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUserRole } from '@/hooks/useUserRole';
import { useUserCompany } from '@/hooks/useUserCompany';
import type { EnergyFilters } from '@/types/energy';
import { trackDashboardDateRange, trackDashboardLiveMode } from '@/lib/analytics';

const DATE_RANGE_STORAGE_KEY = 'ht-energy-date-range';

// Valid date range values
const VALID_RANGES = ['today', '48h', '3d', '5d', '7d', '30d', '60d', '90d'];

/**
 * Dashboard Page - Ecoluxe Off-Grid Command Center
 *
 * Total energy analytics view showing solar generation,
 * battery status, cost savings, and environmental impact.
 *
 * This is a full-width analytics view without the unit list sidebar.
 */
export function Dashboard() {
  const isMobile = useIsMobile();
  const { isLiveDemoEnabled } = useLiveDemoContext();
  const { isSuperadmin } = useUserRole();
  const { company } = useUserCompany();

  // Track previous date range before live mode was enabled
  const previousDateRangeRef = useRef<EnergyFilters['dateRange'] | null>(null);

  // Track last updated time from EnergyDashboard
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Initialize date range from session storage
  const [dateRange, setDateRange] = useState<EnergyFilters['dateRange']>(() => {
    const stored = sessionStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (stored && VALID_RANGES.includes(stored)) {
      return stored as EnergyFilters['dateRange'];
    }
    return '30d';
  });

  // When live mode is enabled, force date range to 'today'
  // When disabled, restore previous date range
  useEffect(() => {
    if (isLiveDemoEnabled) {
      trackDashboardLiveMode(true);
      // Save current date range before switching to today
      if (dateRange !== 'today') {
        previousDateRangeRef.current = dateRange;
      }
      setDateRange('today');
    } else if (previousDateRangeRef.current) {
      trackDashboardLiveMode(false);
      // Restore previous date range when live mode is disabled
      setDateRange(previousDateRangeRef.current);
      previousDateRangeRef.current = null;
    }
  }, [isLiveDemoEnabled]);

  // Persist date range to session storage when it changes (but not when live mode forces it)
  useEffect(() => {
    if (!isLiveDemoEnabled) {
      sessionStorage.setItem(DATE_RANGE_STORAGE_KEY, dateRange);
    }
  }, [dateRange, isLiveDemoEnabled]);

  // Wrap setDateRange to track analytics for user-initiated changes
  const handleDateRangeChange = useCallback((range: EnergyFilters['dateRange']) => {
    trackDashboardDateRange(range);
    setDateRange(range);
  }, []);
  const {
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedIndustryTypes,
    setSelectedModels,
  } = useFilters();

  // Reset filters to empty - only reset filters the user has access to
  // Industry-restricted users keep their industry, non-superadmins keep their company
  const handleResetFilters = useCallback(() => {
    setDateRange('30d');

    // Models - always clearable by all users
    setSelectedModels([]);

    // Groups - always clearable by all users
    setSelectedGroupIds([]);

    // Industry Types - only clear if user has access (superadmin or internal user without industry restriction)
    if (isSuperadmin || company?.is_internal || !company?.industry) {
      setSelectedIndustryTypes([]);
    }
    // Otherwise keep their restricted industry type

    // Companies - only clear if user is superadmin or internal (can see all companies)
    if (isSuperadmin || company?.is_internal) {
      setSelectedCompanyIds([]);
    }
    // Otherwise keep their company selected
  }, [setSelectedCompanyIds, setSelectedGroupIds, setSelectedIndustryTypes, setSelectedModels, isSuperadmin, company]);

  return (
    <div className="flex flex-col h-dvh">
      <Header
        mobileTitle="Ecoluxe Command"
        mobileMenuActions={
          <MobileMenuDateControls
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onResetFilters={handleResetFilters}
            dateRangeLocked={isLiveDemoEnabled}
          />
        }
      />
      {/* Status bar - only on desktop, mobile has the date range in the mobile menu */}
      {!isMobile && (
        <EnergyStatusBar
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onResetFilters={handleResetFilters}
          dateRangeLocked={isLiveDemoEnabled}
          lastUpdated={lastUpdated}
        />
      )}
      <main className="flex-1 overflow-auto bg-background min-h-0">
        <EnergyDashboard
          dateRange={dateRange}
          onLastUpdatedChange={setLastUpdated}
        />
      </main>
    </div>
  );
}
